"use client";

import { useState, useEffect } from "react";
import { HeroCarousel, type HeroSlide, type HeroSlideMedia } from "./HeroCarousel";
import { api, type EventoDto, type ApprofondimentoDto, type EventoEsternoDto } from "@/lib/api";
import { resolveMediaUrl, resolveImageUrl, isImageMedia } from "@/lib/media-url";
import { isScreenshotMedia } from "@/lib/hero-image-preference";
import type { HeroCarouselManualItem } from "@/components/admin/builder/types";

/**
 * Wrapper client-side per il blocco heroCarousel.
 * Gestisce sia la modalità automatica (logica identica alla homepage originale)
 * sia la modalità manuale (carica per slug nell'ordine configurato).
 */

interface HeroCarouselBlockProps {
  mode: "automatic" | "manual";
  /** Variante grafica: "full" (default, full-viewport) o "compact" (più discreta). */
  variant?: "full" | "compact";
  showApertura?: boolean;
  aperturaPosition?: number;
  maxEventi?: number;
  maxApprofondimentiPerCat?: number;
  manualItems?: HeroCarouselManualItem[];
  aperturaTitle?: string;
  aperturaSubtitle?: string;
  aperturaCta?: string;
  aperturaCtaUrl?: string;
  aperturaImage?: string;
  aperturaMedia?: HeroSlideMedia;
  eventoLayout?: EventoLayout;
}

type EventoLayout = "media-and-photo" | "media-only" | "photo-only" | "gallery-only";

function toPlainText(input?: string | null): string {
  if (!input) return "";
  return input
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function toExcerpt(input?: string | null, maxLen = 320): string | undefined {
  const text = toPlainText(input);
  if (!text) return undefined;
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
}

/** Vero se il media configurato ha contenuto utile da renderizzare. */
function hasAperturaMedia(m: HeroSlideMedia | undefined): m is HeroSlideMedia {
  if (!m) return false;
  if (m.type === "quote") return !!m.quote;
  if (m.type === "gallery") return (m.images?.length ?? 0) > 0;
  return !!m.url;
}

function eventoToSlide(ev: EventoDto, layout: EventoLayout = "media-and-photo"): HeroSlide {
  const img = isImageMedia(ev.foto) ? ev.foto : isImageMedia(ev.volantino) ? ev.volantino : null;
  const d = new Date(ev.data);
  const dateStr = d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  // Video da embeddare, per priorità:
  //  1. primo mediaItem VIDEO_URL (URL esterno, tipicamente YouTube)
  //  2. primo mediaItem VIDEO_FILE (file caricato)
  //  3. ev.videoUrl legacy
  const videoUrlItem = ev.mediaItems?.find((m) => m.mediaType === "VIDEO_URL" && m.externalUrl);
  const videoFileItem = ev.mediaItems?.find((m) => m.mediaType === "VIDEO_FILE" && m.media);
  let videoMedia: HeroSlideMedia | undefined;
  if (videoUrlItem?.externalUrl) {
    videoMedia = { type: "video", url: videoUrlItem.externalUrl, title: videoUrlItem.title ?? ev.titolo };
  } else if (videoFileItem?.media) {
    const url = resolveMediaUrl(videoFileItem.media);
    if (url) videoMedia = { type: "video", url, title: videoFileItem.title ?? ev.titolo };
  } else if (ev.videoUrl) {
    videoMedia = { type: "video", url: ev.videoUrl, title: ev.titolo };
  }

  // Gallery costruita da: ev.gallery + foto + volantino + mediaItems PHOTO/GALLERY
  // Deduplicata e con la foto principale in prima posizione.
  const galleryUrls: string[] = [];
  const seen = new Set<string>();
  const push = (u: string | null | undefined) => {
    if (!u || seen.has(u)) return;
    seen.add(u);
    galleryUrls.push(u);
  };
  push(resolveMediaUrl(img) ?? null);
  for (const g of ev.gallery ?? []) push(resolveMediaUrl(g));
  for (const mi of ev.mediaItems ?? []) {
    if (mi.mediaType === "PHOTO" || mi.mediaType === "GALLERY") push(resolveMediaUrl(mi.media));
  }
  const galleryMedia: HeroSlideMedia | undefined = galleryUrls.length > 0
    ? { type: "gallery", images: galleryUrls, title: ev.titolo }
    : undefined;

  // Applica il layout richiesto. Se il layout non ha elementi utili,
  // ripiega su "photo-only" (immagine principale) e, se neanche quella
  // esiste, mostra comunque la slide testuale senza media.
  let slideMedia: HeroSlideMedia | undefined;
  let slideImage: string | undefined;
  const photoUrl = resolveMediaUrl(img) ?? undefined;

  switch (layout) {
    case "media-only":
      if (videoMedia) {
        slideMedia = videoMedia;
        slideImage = undefined;
      } else if (galleryMedia) {
        // Fallback intermedio: niente video ma abbiamo foto → usa gallery
        slideMedia = galleryMedia;
        slideImage = undefined;
      } else {
        // Fallback finale: photo-only
        slideMedia = undefined;
        slideImage = photoUrl;
      }
      break;
    case "photo-only":
      slideMedia = undefined;
      slideImage = photoUrl;
      break;
    case "gallery-only":
      if (galleryMedia) {
        slideMedia = galleryMedia;
        slideImage = undefined;
      } else {
        // Fallback: photo-only (niente gallery disponibile)
        slideMedia = undefined;
        slideImage = photoUrl;
      }
      break;
    case "media-and-photo":
    default:
      if (videoMedia || photoUrl) {
        slideMedia = videoMedia;
        slideImage = photoUrl;
      } else if (galleryMedia) {
        // Niente video e niente foto singola: usiamo la gallery
        slideMedia = galleryMedia;
        slideImage = undefined;
      }
      break;
  }

  return {
    type: "evento",
    title: ev.titolo,
    subtitle: `${dateStr}${ev.luogo ? " — " + ev.luogo : ""}`,
    imageUrl: slideImage,
    href: `/eventi/${ev.slug}`,
    cta: "Scopri l'evento",
    badge: ev.daNonPerdere ? "In evidenza" : d >= new Date() ? "Prossimo evento" : "Evento recente",
    media: slideMedia,
  };
}

/** Evento esterno (segnalato) come slide del carosello: riusa il rendering
 *  "evento" con badge "Segnalato" e link esterno alla fonte. */
/** Un evento (ISO date) è passato se la sua data è precedente a oggi (00:00).
 *  Gli eventi del giorno stesso NON sono passati (coerente col backend
 *  `data >= oggi`). Usato per nascondere gli eventi segnalati passati dall'hero. */
function isPastDate(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function eventoEsternoToSlide(e: EventoEsternoDto): HeroSlide {
  const mediaId = e.fotoMediaId ?? e.volantinoMediaId;
  const poster = mediaId
    ? resolveMediaUrl({ id: mediaId } as unknown as Parameters<typeof resolveMediaUrl>[0])
    : undefined;
  const d = e.data ? new Date(e.data) : null;
  const dateStr = d ? d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";
  const ora = e.oraInizio ? ` · ore ${e.oraInizio.slice(0, 5)}` : "";
  return {
    type: "evento",
    title: e.title,
    subtitle: `${dateStr}${ora}${e.location ? " — " + e.location : ""}`,
    imageUrl: poster ?? undefined,
    // urlEsterna (linkUrl) vince; altrimenti la nostra pagina di dettaglio.
    href: e.linkUrl || `/eventi-segnalati/${e.slug}`,
    cta: e.linkUrl ? "Vai all'evento" : "Scopri",
    badge: "Segnalato",
  };
}

function approfondimentoToSlide(a: ApprofondimentoDto): HeroSlide {
  const cat = a.categories?.find((c) => c.slug !== "approfondimenti") ?? a.categories?.[0];
  return {
    type: "approfondimento",
    title: a.titolo,
    subtitle: cat?.slug !== "approfondimenti" ? cat?.name : undefined,
    categorySlug: cat?.slug !== "approfondimenti" ? cat?.slug : undefined,
    excerpt: toExcerpt(a.contenuto),
    source: a.fonte || undefined,
    authors: a.autori,
    // heroImage e' il campo derivato dal backend che risolve gia' la
    // preferenza editoriale (AUTO=immagine??screenshot, USE_SCREENSHOT, HIDE);
    // l'ordine fallback (heroImage ?? immagine ?? screenshot) copre anche i
    // DTO legacy cached prima dell'introduzione di heroImage.
    imageUrl: resolveImageUrl(a.heroImage ?? a.immagine ?? a.screenshot) ?? undefined,
    imageIsScreenshot: isScreenshotMedia(a.heroImage ?? a.immagine ?? a.screenshot) || (!a.immagine && !!a.screenshot),
    approfondimentoId: a.id,
    href: `/approfondimenti/${a.slug}`,
    cta: "Leggi",
    badge: (cat?.slug !== "approfondimenti" ? cat?.name : undefined) || "Approfondimento",
  };
}

export function HeroCarouselBlock({
  mode,
  variant = "full",
  showApertura = true,
  aperturaPosition = 0,
  maxEventi = 2,
  maxApprofondimentiPerCat = 3,
  manualItems = [],
  aperturaTitle,
  aperturaSubtitle,
  aperturaCta,
  aperturaCtaUrl,
  aperturaImage,
  aperturaMedia,
  eventoLayout = "media-and-photo",
}: HeroCarouselBlockProps) {
  const [slides, setSlides] = useState<HeroSlide[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadAutomatic() {
      try {
        const [eventiUpcoming, eventiPast, approfondimenti, daNonPerdere] = await Promise.all([
          api.eventi.list({ size: 6, filter: "upcoming" }),
          api.eventi.list({ size: 10, filter: "past" }),
          api.approfondimenti.list({ size: 50 }),
          api.eventi.daNonPerdere(maxEventi),
        ]);

        // Hero eventi: prima "da non perdere", poi upcoming, poi past
        const heroEventi: EventoDto[] = [];
        const seenIds = new Set<number>();
        for (const ev of [...daNonPerdere, ...eventiUpcoming.items, ...eventiPast.items]) {
          if (seenIds.has(ev.id)) continue;
          seenIds.add(ev.id);
          heroEventi.push(ev);
          if (heroEventi.length >= maxEventi) break;
        }

        // Approfondimenti: interleave per categoria
        const catBuckets = new Map<string, ApprofondimentoDto[]>();
        const catOrder: string[] = [];
        for (const a of approfondimenti.items) {
          const cat = a.categories?.find((c) => c.slug !== "approfondimenti") ?? a.categories?.[0];
          const key = cat?.slug || "__none__";
          if (!catBuckets.has(key)) { catBuckets.set(key, []); catOrder.push(key); }
          const bucket = catBuckets.get(key)!;
          if (bucket.length < maxApprofondimentiPerCat) bucket.push(a);
        }

        const heroApproSlice: ApprofondimentoDto[] = [];
        for (let round = 0; round < maxApprofondimentiPerCat; round++) {
          for (const k of catOrder) {
            const items = catBuckets.get(k)!;
            if (round < items.length) heroApproSlice.push(items[round]);
          }
        }

        // Carica dettagli completi per approfondimenti
        const approfondimentiDetailed = await Promise.all(
          heroApproSlice.map(async (a) => {
            try {
              const full = await api.approfondimenti.bySlug(a.slug);
              return {
                ...a,
                contenuto: full.contenuto ?? a.contenuto,
                fonte: full.fonte ?? a.fonte,
                autori: full.autori?.length ? full.autori : a.autori,
                immagine: full.immagine ?? a.immagine,
                screenshot: full.screenshot ?? a.screenshot,
                heroImage: full.heroImage ?? a.heroImage,
              };
            } catch { return a; }
          }),
        );

        if (cancelled) return;

        // Passa tutto a HeroCarousel per buildSlides
        setSlides(null); // Usiamo le props dirette
        // Ma in realtà vogliamo costruire slides qui perché HeroCarousel.buildSlides è interna
        // Ricostruiamo le slides in modo equivalente

        const result: HeroSlide[] = [];
        heroEventi.forEach((ev) => result.push(eventoToSlide(ev, eventoLayout)));
        approfondimentiDetailed.forEach((a) => result.push(approfondimentoToSlide(a)));

        // Insert apertura at desired position
        if (showApertura) {
          const aperturaSlide: HeroSlide = {
            type: "apertura",
            title: aperturaTitle || "Una fede che\ndiventa cultura",
            subtitle: aperturaSubtitle ?? "Centro Culturale Blaise Pascal — Reggio Emilia, dal 1986",
            imageUrl: aperturaImage || undefined,
            href: aperturaCtaUrl || "/chi-siamo",
            cta: aperturaCta || "Scopri chi siamo",
            media: hasAperturaMedia(aperturaMedia) ? aperturaMedia : undefined,
          };
          const pos = Math.max(0, Math.min(aperturaPosition, result.length));
          result.splice(pos, 0, aperturaSlide);
        }

        setSlides(result);
      } catch {
        if (!cancelled) setError(true);
      }
    }

    async function loadManual() {
      try {
        const result: HeroSlide[] = [];

        for (const item of manualItems) {
          try {
            if (item.type === "evento") {
              const ev = await api.eventi.bySlug(item.slug);
              result.push(eventoToSlide(ev, eventoLayout));
            } else if (item.type === "eventoEsterno") {
              const e = await api.eventiEsterni.bySlug(item.slug);
              // Evento segnalato passato → non entra nell'hero (resta in archivio).
              if (!isPastDate(e.data)) {
                result.push(eventoEsternoToSlide(e));
              }
            } else {
              const a = await api.approfondimenti.bySlug(item.slug);
              result.push(approfondimentoToSlide(a));
            }
          } catch {
            // Skip elementi non trovati
          }
        }

        // Insert apertura at desired position
        if (showApertura) {
          const aperturaSlide: HeroSlide = {
            type: "apertura",
            title: aperturaTitle || "Una fede che\ndiventa cultura",
            subtitle: aperturaSubtitle ?? "Centro Culturale Blaise Pascal — Reggio Emilia, dal 1986",
            imageUrl: aperturaImage || undefined,
            href: aperturaCtaUrl || "/chi-siamo",
            cta: aperturaCta || "Scopri chi siamo",
            media: hasAperturaMedia(aperturaMedia) ? aperturaMedia : undefined,
          };
          const pos = Math.max(0, Math.min(aperturaPosition, result.length));
          result.splice(pos, 0, aperturaSlide);
        }

        if (!cancelled) setSlides(result);
      } catch {
        if (!cancelled) setError(true);
      }
    }

    if (mode === "automatic") {
      loadAutomatic();
    } else {
      loadManual();
    }

    return () => { cancelled = true; };
  }, [mode, showApertura, aperturaPosition, maxEventi, maxApprofondimentiPerCat, manualItems, aperturaTitle, aperturaSubtitle, aperturaCta, aperturaCtaUrl, aperturaImage, aperturaMedia, eventoLayout]);

  if (error) return null;

  if (!slides) {
    // Skeleton loading — altezza allineata alla variante scelta per evitare layout shift.
    const skelH = variant === "compact"
      ? "min-h-[360px] sm:min-h-[48vh]"
      : "min-h-[82vh] sm:min-h-[78vh]";
    return (
      <div className={`w-full ${skelH} rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#1a3a5c] to-[#0f2a45] animate-pulse flex items-center justify-center mx-auto max-w-[calc(100%-1rem)] sm:max-w-[calc(100%-2rem)] mt-2 sm:mt-3`}>
        <div className="text-white/30 text-center">
          <div className="text-4xl mb-3">🎠</div>
          <p className="text-sm">Caricamento carosello…</p>
        </div>
      </div>
    );
  }

  if (slides.length === 0) return null;

  return <HeroCarousel slides={slides} variant={variant} />;
}

